import { ApiError } from '@platform/api-client';
import { INLINE_IMAGE_TYPES, MAX_INLINE_IMAGE_BYTES, sanitizeMailHtml } from '@platform/ui';

const DATA_IMAGE = new RegExp(`^data:(${INLINE_IMAGE_TYPES.join('|')});base64,[A-Za-z0-9+/]+={0,2}$`, 'i');
const CID_IMAGE = /^cid:([^\s"'<>\\]{1,512})$/i;

/** The composer has no viewer iframe CSP: images must be inert data or authenticated local CID parts. */
export async function loadDraftHtml(html: string, image: (cid: string) => Promise<Blob>): Promise<string> {
  const template = document.createElement('template'); // Inert parsing: no image requests before validation.
  template.innerHTML = sanitizeMailHtml(html).html;
  const loaded = new Map<string, string>();
  for (const node of template.content.querySelectorAll('img')) {
    const src = node.getAttribute('src') ?? '';
    if (DATA_IMAGE.test(src)) continue;
    const cid = CID_IMAGE.exec(src)?.[1];
    if (!cid) { node.remove(); continue; }
    let data = loaded.get(cid);
    if (!data) {
      const blob = await image(cid);
      if (!(INLINE_IMAGE_TYPES as readonly string[]).includes(blob.type) || blob.size > MAX_INLINE_IMAGE_BYTES) {
        throw new ApiError(415, 'draft_image_unavailable', 'Draft image unavailable');
      }
      data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === 'string' && DATA_IMAGE.test(reader.result)
          ? resolve(reader.result) : reject(new ApiError(415, 'draft_image_unavailable', 'Draft image unavailable'));
        reader.onerror = () => reject(reader.error ?? new Error('Draft image unavailable'));
        reader.readAsDataURL(blob);
      });
      loaded.set(cid, data);
    }
    node.setAttribute('src', data);
  }
  return template.innerHTML;
}
